import { ensureDeliveryLocation } from '@theme/delivery-location-service';

(function() {
  'use strict';

  class SolutionsTabsSection {
    constructor(section) {
      this.section = section;
      this.panelWrapper = section.querySelector('.solutions-panel-wrapper');
      this.tabTriggers = Array.from(section.querySelectorAll('.solutions-tab-trigger'));
      this.mobileTriggers = Array.from(section.querySelectorAll('.tab-mobile-trigger'));
      this.tabPanels = Array.from(section.querySelectorAll('.solutions-tab-panel'));
      this.tabLists = Array.from(section.querySelectorAll('[data-tablist]'));
      this.tabVisibleContainer = section.querySelector('.tab-visible-container');
      this.tabMobileNav = section.querySelector('.tab-mobile-nav');
      this.progressFill = section.querySelector('.tab-progress-fill');
      this.carouselControls = section.querySelector('.carousel-controls');
      this.prevBtn = section.querySelector('.prev-btn');
      this.nextBtn = section.querySelector('.next-btn');
      this.statusRegion = section.querySelector('[data-cart-status]');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.currentTabIndex = 0;
      this.resizeTimeout = null;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.labels = {
        addToCart: section.dataset.addToCartLabel || 'Add to cart',
        addingToCart: section.dataset.addingToCartLabel || 'Adding...',
        addedToCart: section.dataset.addedToCartLabel || 'Added to cart',
        addToCartError: section.dataset.addToCartErrorLabel || 'Error adding to cart',
        soldOut: section.dataset.soldOutLabel || 'Sold out',
        saleBadge: section.dataset.saleBadgeLabel || 'Sale',
      };

      if (!this.panelWrapper || this.tabPanels.length === 0) {
        this.ready = false;
        return;
      }

      this.ready = true;
      this.bindEvents();
      this.switchTab(0);
      this.updateScrollProgress();
    }

    bindEvents() {
      this.tabTriggers.forEach((button, index) => {
        button.addEventListener('click', () => this.switchTab(index), { signal: this.signal });
      });

      this.mobileTriggers.forEach((button, index) => {
        button.addEventListener('click', () => this.switchTab(index), { signal: this.signal });
      });

      this.tabLists.forEach((tabList) => {
        tabList.addEventListener('keydown', (event) => this.handleTabKeydown(event), { signal: this.signal });
      });

      this.tabPanels.forEach((panel) => {
        panel.addEventListener('scroll', () => {
          if (panel.classList.contains('active')) {
            this.updateCarouselButtonsState(panel);
          }
        }, { passive: true, signal: this.signal });
      });

      if (this.tabVisibleContainer) {
        this.tabVisibleContainer.addEventListener('scroll', () => this.updateScrollProgress(), {
          passive: true,
          signal: this.signal,
        });
      }

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollCarousel(-1), { signal: this.signal });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollCarousel(1), { signal: this.signal });
      }

      this.section.addEventListener('click', (event) => this.handleSectionClick(event), { signal: this.signal });
      this.section.addEventListener('change', (event) => this.handleSectionChange(event), { signal: this.signal });
      window.addEventListener('resize', () => this.handleResize(), { signal: this.signal });
    }

    handleSectionClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const decreaseButton = target.closest('.qty-decrease');
      if (decreaseButton) {
        this.handleQuantityChange(decreaseButton, -1);
        return;
      }

      const increaseButton = target.closest('.qty-increase');
      if (increaseButton) {
        this.handleQuantityChange(increaseButton, 1);
        return;
      }

      const variantPill = target.closest('.variant-pill');
      if (variantPill instanceof HTMLButtonElement && !variantPill.disabled) {
        this.handleVariantChange(variantPill);
        return;
      }

      const addToCartButton = target.closest('.atc-btn');
      if (addToCartButton instanceof HTMLButtonElement && !addToCartButton.disabled) {
        event.preventDefault();
        this.handleAddToCart(addToCartButton);
      }
    }

    handleSectionChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.classList.contains('qty-input')) {
        return;
      }

      this.sanitizeQuantityInput(target);
    }

    handleTabKeydown(event) {
      const target = event.target;
      const currentList = event.currentTarget;

      if (!(target instanceof HTMLButtonElement) || !(currentList instanceof Element)) {
        return;
      }

      const tabs = Array.from(currentList.querySelectorAll('[role="tab"]'));
      const currentIndex = tabs.indexOf(target);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      const nextTab = tabs[nextIndex];
      const sectionIndex = parseInt(nextTab.dataset.index || '0', 10);
      this.switchTab(sectionIndex);
      nextTab.focus();
    }

    switchTab(index) {
      if (Number.isNaN(index) || index < 0 || index >= this.tabPanels.length) return;

      this.currentTabIndex = index;

      this.tabTriggers.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === index;
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-selected', String(isActive));
        button.tabIndex = isActive ? 0 : -1;
      });

      this.mobileTriggers.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === index;
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-selected', String(isActive));
        button.tabIndex = isActive ? 0 : -1;
      });

      this.tabPanels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;
        panel.classList.toggle('active', isActive);
        panel.hidden = !isActive;
        panel.setAttribute('aria-hidden', String(!isActive));

        if (isActive) {
          panel.scrollLeft = 0;
        }
      });

      const activeMobileButton = this.mobileTriggers[index];
      if (activeMobileButton) {
        this.scrollTabIntoView(activeMobileButton);
      }

      window.requestAnimationFrame(() => {
        this.setupCarousel();
        this.updateScrollProgress();
      });
    }

    handleVariantChange(pill) {
      const card = pill.closest('.solution-card');
      if (!(card instanceof HTMLElement) || card.dataset.cardBehavior !== 'solution-tabs') return;

      const pills = Array.from(card.querySelectorAll('.variant-pill'));
      pills.forEach((button) => {
        const isActive = button === pill;
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });

      const price = parseInt(pill.dataset.price || '0', 10);
      const comparePrice = parseInt(pill.dataset.comparePrice || '0', 10);
      const isAvailable = pill.dataset.available === 'true';
      const imageSrc = pill.dataset.imageSrc || '';
      const imageAlt = pill.dataset.imageAlt || '';
      const priceDisplay = pill.dataset.variantPriceDisplay || '';
      const comparePriceDisplay = pill.dataset.variantComparePriceDisplay || '';
      const atcButton = card.querySelector('.atc-btn');

      if (!(atcButton instanceof HTMLButtonElement)) return;

      if (imageSrc) {
        const mainImage = card.querySelector('[data-main-image]');
        if (mainImage instanceof HTMLImageElement) {
          mainImage.src = imageSrc;
          if (imageAlt) {
            mainImage.alt = imageAlt;
          }
        }
      }

      this.updateCardPrice(card, priceDisplay, comparePriceDisplay, comparePrice > price);
      this.updateSoldOutBadge(card, !isAvailable);

      atcButton.dataset.variantId = pill.dataset.variantId || '';
      atcButton.disabled = !isAvailable;
      atcButton.textContent = isAvailable ? this.labels.addToCart : this.labels.soldOut;
    }

    updateCardPrice(card, priceDisplay, comparePriceDisplay, isOnSale) {
      const priceElement = card.querySelector('.solution-card__price-wrapper [data-price-display]');
      const comparePriceElement = card.querySelector('.solution-card__price-wrapper [data-compare-price-display]');

      if (priceElement instanceof HTMLElement && priceDisplay) {
        priceElement.textContent = priceDisplay;
        priceElement.classList.toggle('solution-card__price--sale', isOnSale);
      }

      if (comparePriceElement instanceof HTMLElement) {
        if (isOnSale && comparePriceDisplay) {
          comparePriceElement.textContent = comparePriceDisplay;
          comparePriceElement.hidden = false;
        } else {
          comparePriceElement.textContent = '';
          comparePriceElement.hidden = true;
        }
      }

      this.updateSaleBadge(card, isOnSale);
    }

    updateSaleBadge(card, showBadge) {
      // TT: Coming Soon badge always wins; don't let variant-switch JS re-add a Sale badge over it.
      if (card.dataset.comingSoon === 'true') return;

      const badges = card.querySelector('.solution-card__badges');
      if (!(badges instanceof HTMLElement)) return;

      const existingBadge = badges.querySelector('.solution-card__badge--sale');
      if (showBadge) {
        if (!existingBadge) {
          const badge = document.createElement('span');
          badge.className = 'solution-card__badge solution-card__badge--sale';
          badge.textContent = this.labels.saleBadge;
          badges.prepend(badge);
        }
      } else if (existingBadge) {
        existingBadge.remove();
      }
    }

    updateSoldOutBadge(card, showBadge) {
      // TT: Coming Soon badge always wins; don't let variant-switch JS re-add a Sold Out badge over it.
      if (card.dataset.comingSoon === 'true') return;

      const badges = card.querySelector('.solution-card__badges');
      if (!(badges instanceof HTMLElement)) return;

      const existingBadge = badges.querySelector('.solution-card__badge--sold-out');
      if (showBadge) {
        if (!existingBadge) {
          const badge = document.createElement('span');
          badge.className = 'solution-card__badge solution-card__badge--sold-out';
          badge.textContent = this.labels.soldOut;
          badges.appendChild(badge);
        }
      } else if (existingBadge) {
        existingBadge.remove();
      }
    }

    handleQuantityChange(button, delta) {
      const card = button.closest('.solution-card');
      if (!(card instanceof HTMLElement) || card.dataset.cardBehavior !== 'solution-tabs') return;

      const input = card.querySelector('.qty-input');
      if (!(input instanceof HTMLInputElement)) return;

      const currentValue = parseInt(input.value || '1', 10) || 1;
      const nextValue = Math.max(1, Math.min(99, currentValue + delta));
      input.value = String(nextValue);
    }

    sanitizeQuantityInput(input) {
      const nextValue = parseInt(input.value || '1', 10);
      input.value = String(Math.max(1, Math.min(99, Number.isNaN(nextValue) ? 1 : nextValue)));
    }

    async handleAddToCart(button) {
      const card = button.closest('.solution-card');
      if (!(card instanceof HTMLElement) || card.dataset.cardBehavior !== 'solution-tabs') return;

      const qtyInput = card.querySelector('.qty-input');
      if (!(qtyInput instanceof HTMLInputElement)) return;

      this.sanitizeQuantityInput(qtyInput);

      const quantity = parseInt(qtyInput.value || '1', 10) || 1;
      const variantId = button.dataset.variantId;

      if (!variantId) {
        this.announce(this.labels.addToCartError);
        return;
      }

      button.classList.add('loading');
      button.disabled = true;
      button.textContent = this.labels.addingToCart;

      if (!(await ensureDeliveryLocation())) {
        button.classList.remove('loading');
        button.disabled = false;
        button.textContent = this.labels.addToCart;
        return;
      }

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: quantity,
              },
            ],
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || this.labels.addToCartError);
        }

        await response.json();
        await this.refreshCartDrawer();
        this.openCartDrawer();
        this.announce(this.labels.addedToCart);

        button.classList.remove('loading');
        button.classList.add('success');
        button.textContent = this.labels.addedToCart;

        window.setTimeout(() => {
          button.classList.remove('success');
          button.disabled = false;
          button.textContent = this.labels.addToCart;
          qtyInput.value = '1';
        }, 1200);
      } catch (error) {
        console.error('Add to cart error:', error);
        this.announce(error instanceof Error ? error.message : this.labels.addToCartError);
        button.classList.remove('loading');
        button.textContent = this.labels.addToCartError;

        window.setTimeout(() => {
          button.disabled = false;
          button.textContent = this.labels.addToCart;
        }, 1500);
      }
    }

    async refreshCartDrawer() {
      try {
        const cartItemsComponent = document.querySelector('cart-items-component[data-drawer]');
        if (!(cartItemsComponent instanceof HTMLElement)) {
          await this.updateCartCount();
          return;
        }

        const sectionId = cartItemsComponent.dataset.sectionId;
        if (!sectionId) {
          await this.updateCartCount();
          return;
        }

        const [sectionResponse, cartResponse] = await Promise.all([
          fetch(window.Shopify.routes.root + '?section_id=' + sectionId),
          fetch(window.Shopify.routes.root + 'cart.js'),
        ]);

        const [html, cart] = await Promise.all([
          sectionResponse.text(),
          cartResponse.json(),
        ]);

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const currentDrawerInner = document.querySelector('.cart-drawer__inner[data-hydration-key="cart-drawer-inner"]');
        const newDrawerInner = doc.querySelector('.cart-drawer__inner[data-hydration-key="cart-drawer-inner"]');

        if (currentDrawerInner instanceof HTMLElement && newDrawerInner instanceof HTMLElement) {
          currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
        }

        this.updateCartCountElements(cart.item_count);
      } catch (error) {
        console.error('Cart refresh error:', error);
        await this.updateCartCount();
      }
    }

    async updateCartCount() {
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await response.json();
        this.updateCartCountElements(cart.item_count);
      } catch (error) {
        console.error('Cart count update error:', error);
      }
    }

    updateCartCountElements(count) {
      const selectors = [
        '[data-cart-count]',
        'cart-icon [data-cart-count]',
        '.cart-count-bubble',
        '#cart-icon-bubble',
      ];

      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => {
          if (!(element instanceof HTMLElement)) return;
          element.textContent = String(count);
          if (count > 0) {
            element.classList.remove('hidden');
            element.classList.remove('visually-hidden');
            element.style.display = '';
          }
        });
      });
    }

    openCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }

      if (cartDrawer && typeof cartDrawer.showDialog === 'function') {
        cartDrawer.showDialog();
        return;
      }

      const cartTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
      if (cartTrigger instanceof HTMLElement) {
        cartTrigger.click();
      }
    }

    announce(message) {
      if (!(this.statusRegion instanceof HTMLElement)) return;
      this.statusRegion.textContent = '';
      window.requestAnimationFrame(() => {
        this.statusRegion.textContent = message;
      });
    }

    scrollTabIntoView(button) {
      if (!this.tabVisibleContainer) return;

      const container = this.tabVisibleContainer;
      const tabLeft = button.offsetLeft;
      const tabRight = tabLeft + button.offsetWidth;
      const visibleLeft = container.scrollLeft;
      const visibleRight = visibleLeft + container.clientWidth;

      if (tabLeft < visibleLeft) {
        container.scrollTo({
          left: tabLeft - 12,
          behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
        });
      } else if (tabRight > visibleRight) {
        container.scrollTo({
          left: tabRight - container.clientWidth + 12,
          behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
        });
      }
    }

    setupCarousel() {
      const activePanel = this.tabPanels[this.currentTabIndex];
      if (!(activePanel instanceof HTMLElement) || !this.carouselControls || !this.prevBtn || !this.nextBtn) {
        return;
      }

      const cards = activePanel.querySelectorAll('.solution-card');
      const cardsPerView = this.getCardsPerView();
      const hasOverflow = cards.length > cardsPerView && activePanel.scrollWidth > activePanel.clientWidth + 4;

      this.carouselControls.classList.toggle('active', hasOverflow);

      if (!hasOverflow) {
        this.prevBtn.disabled = true;
        this.nextBtn.disabled = true;
        return;
      }

      this.updateCarouselButtonsState(activePanel);
    }

    updateCarouselButtonsState(panel) {
      if (!this.prevBtn || !this.nextBtn) return;

      const maxScrollLeft = Math.max(0, panel.scrollWidth - panel.clientWidth);
      this.prevBtn.disabled = panel.scrollLeft <= 4;
      this.nextBtn.disabled = maxScrollLeft <= 0 || panel.scrollLeft >= maxScrollLeft - 4;
    }

    scrollCarousel(direction) {
      const activePanel = this.tabPanels[this.currentTabIndex];
      if (!(activePanel instanceof HTMLElement)) return;

      const scrollAmount = this.getScrollAmount(activePanel) * direction;
      activePanel.scrollBy({
        left: scrollAmount,
        behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
      });
    }

    getScrollAmount(panel) {
      const firstCard = panel.querySelector('.solution-card');
      if (!(firstCard instanceof HTMLElement)) {
        return panel.clientWidth;
      }

      const styles = window.getComputedStyle(panel);
      const gap = parseFloat(styles.columnGap || styles.gap || '16');
      return (firstCard.getBoundingClientRect().width + gap) * this.getCardsPerView();
    }

    getCardsPerView() {
      const width = window.innerWidth;
      if (width >= 1024) return 4;
      if (width >= 768) return 3;
      if (width >= 375) return 2;
      return 1;
    }

    updateScrollProgress() {
      if (!this.tabVisibleContainer || !this.progressFill || !this.tabMobileNav) return;

      if (window.innerWidth > 400) {
        this.progressFill.style.width = '0%';
        return;
      }

      const scrollWidth = this.tabMobileNav.scrollWidth - this.tabVisibleContainer.clientWidth;
      if (scrollWidth <= 0) {
        this.progressFill.style.width = '0%';
        return;
      }

      const progress = (this.tabVisibleContainer.scrollLeft / scrollWidth) * 100;
      this.progressFill.style.width = Math.min(100, Math.max(0, progress)) + '%';
    }

    handleResize() {
      window.clearTimeout(this.resizeTimeout);
      this.resizeTimeout = window.setTimeout(() => {
        this.setupCarousel();
        this.updateScrollProgress();
      }, 150);
    }

    destroy() {
      this.abortController.abort();
      window.clearTimeout(this.resizeTimeout);
    }
  }

  const instances = new WeakMap();

  function findSections(root) {
    const sections = [];
    if (root instanceof Element && root.matches('.solutions-tabs[data-section-id]')) {
      sections.push(root);
    }

    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    scope.querySelectorAll('.solutions-tabs[data-section-id]').forEach((section) => {
      if (!sections.includes(section)) {
        sections.push(section);
      }
    });

    return sections;
  }

  function initSections(root) {
    findSections(root).forEach((section) => {
      if (instances.has(section)) return;
      const instance = new SolutionsTabsSection(section);
      if (instance.ready) {
        instances.set(section, instance);
      }
    });
  }

  function destroySections(root) {
    findSections(root).forEach((section) => {
      const instance = instances.get(section);
      if (!instance) return;
      instance.destroy();
      instances.delete(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSections(document));
  } else {
    initSections(document);
  }

  document.addEventListener('shopify:section:load', (event) => initSections(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroySections(event.target));
})();
